import { useEffect, useState } from 'react';
import type { MetricOrigin, MetricResult, NodeMetricKey, NodeMetrics, TimeUnit } from '@/domain/metrics/contracts';
import { TIME_UNITS, GOAL_TARGET_METRICS, GOAL_PERIODS } from '@/domain/metrics/contracts';
import type { NodeMetricsResult } from '@/domain/metrics/node-metrics';
import { useEditorStore } from '../state/editor-store';
import { formatCount, formatMoneyMinorUnits, formatRatioAsPercent, formatUnknown } from './format';
import { localizeMetricWarning } from '@/app/localization';

const METRIC_LABELS: Record<NodeMetricKey, string> = {
  cost: 'Custo',
  entries: 'Entradas',
  exits: 'Saídas',
  conversion: 'Conversão',
  revenue: 'Receita',
  cpl: 'CPL',
  cac: 'CAC',
  averageTicket: 'Ticket médio',
  averageTime: 'Tempo médio',
  goal: 'Meta',
};

type FieldKind = 'money' | 'count' | 'ratio' | 'time' | 'goal';
const METRIC_KIND: Record<NodeMetricKey, FieldKind> = {
  cost: 'money',
  entries: 'count',
  exits: 'count',
  conversion: 'ratio',
  revenue: 'money',
  cpl: 'money',
  cac: 'money',
  averageTicket: 'money',
  averageTime: 'time',
  goal: 'goal',
};
const TIME_UNIT_LABELS: Record<TimeUnit, string> = {
  minutes: 'Minutos',
  hours: 'Horas',
  days: 'Dias',
};
const GOAL_PERIOD_LABELS: Record<(typeof GOAL_PERIODS)[number], string> = {
  day: 'Dia',
  week: 'Semana',
  month: 'Mês',
  quarter: 'Trimestre',
  year: 'Ano',
};

function formatByKind(kind: FieldKind, value: number | null): string {
  switch (kind) {
    case 'money':
      return formatMoneyMinorUnits(value);
    case 'ratio':
      return formatRatioAsPercent(value);
    default:
      return formatCount(value);
  }
}

function OriginBadge({ origin }: { origin: MetricOrigin | null }) {
  if (!origin) return null;
  const color = origin === 'manual' ? 'var(--data-manual)' : 'var(--data-calculated)';
  return (
    <span
      className="rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide"
      style={{ color, border: `1px solid ${color}` }}
    >
      {origin === 'manual' ? 'Manual' : 'Calculada'}
    </span>
  );
}

function WarningList({ result }: { result: MetricResult }) {
  if (result.warnings.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {result.warnings.map((warning, index) => (
        <li key={index} className="text-[10px] text-[#eaa52d]">
          {localizeMetricWarning(warning)}
        </li>
      ))}
    </ul>
  );
}

function NumericMetricField({
  nodeKey,
  metricKey,
  result,
  kind,
}: {
  nodeKey: string;
  metricKey: NodeMetricKey;
  result: MetricResult;
  kind: 'money' | 'count' | 'ratio';
}) {
  const raw = useEditorStore((state) => (state.graph?.nodes.find((n) => n.id === nodeKey)?.data.metrics as NodeMetrics | undefined)?.[metricKey]);
  const updateMetric = useEditorStore((state) => state.updateNodeMetric);
  const isManual = raw?.mode === 'manual';
  const [draft, setDraft] = useState('');

  useEffect(() => {
    const manualValue = isManual && typeof raw?.manualValue === 'number' ? raw.manualValue : null;
    if (manualValue === null) {
      setDraft('');
      return;
    }
    setDraft(kind === 'money' ? String(manualValue / 100) : kind === 'ratio' ? String(manualValue * 100) : String(manualValue));
  }, [isManual, raw?.manualValue, kind]);

  const commit = () => {
    const parsed = Number(draft.replace(',', '.'));
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      updateMetric(nodeKey, metricKey, { mode: 'manual', manualValue: null });
      return;
    }
    const manualValue = kind === 'money' ? Math.round(parsed * 100) : kind === 'ratio' ? parsed / 100 : parsed;
    updateMetric(nodeKey, metricKey, { mode: 'manual', manualValue });
  };

  const explanationTitle = result.explanation
    ? `${result.explanation.formula}${result.calculatedCandidate !== null ? ` = ${formatByKind(kind, result.calculatedCandidate)}` : ''}`
    : undefined;

  return (
    <div className="space-y-1" data-testid={`metric-field-${metricKey}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-text-muted">{METRIC_LABELS[metricKey]}</span>
        <OriginBadge origin={result.origin} />
      </div>
      {isManual ? (
        <input
          aria-label={METRIC_LABELS[metricKey]}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          className="w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        />
      ) : (
        <div
          className="flex items-center justify-between rounded-md border border-dashed border-border bg-surface-2 px-2 py-1.5 text-xs text-text-secondary"
          title={explanationTitle}
        >
          <span>{result.status === 'known' ? formatByKind(kind, result.effectiveValue as number | null) : formatUnknown()}</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        {!isManual && result.calculatedCandidate !== null && result.status !== 'known' ? (
          <span className="text-[10px] text-text-muted">Candidata: {formatByKind(kind, result.calculatedCandidate)}</span>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="text-[10px] text-text-muted underline decoration-dotted underline-offset-2 hover:text-text-primary"
          onClick={() => updateMetric(nodeKey, metricKey, isManual ? { mode: 'auto', manualValue: null } : { mode: 'manual', manualValue: result.effectiveValue })}
        >
          {isManual ? 'Usar calculada' : 'Substituir manualmente'}
        </button>
      </div>
      <WarningList result={result} />
    </div>
  );
}

function AverageTimeField({ nodeKey, result }: { nodeKey: string; result: NodeMetricsResult['averageTime'] }) {
  const updateMetric = useEditorStore((state) => state.updateNodeMetric);
  const current = result.effectiveValue;
  const [value, setValue] = useState(current ? String(current.value) : '');
  const [unit, setUnit] = useState<TimeUnit>(current?.unit ?? 'minutes');

  useEffect(() => {
    setValue(current ? String(current.value) : '');
    setUnit(current?.unit ?? 'minutes');
  }, [current]);

  const commit = (nextValue: string, nextUnit: TimeUnit) => {
    const parsed = Number(nextValue.replace(',', '.'));
    if (nextValue.trim() === '' || Number.isNaN(parsed)) {
      updateMetric(nodeKey, 'averageTime', { mode: 'manual', manualValue: null });
      return;
    }
    updateMetric(nodeKey, 'averageTime', { mode: 'manual', manualValue: { value: parsed, unit: nextUnit } });
  };

  return (
    <div className="space-y-1">
      <span className="text-[11px] text-text-muted">{METRIC_LABELS.averageTime}</span>
      <div className="flex gap-1">
        <input
          aria-label="Valor do tempo médio"
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => commit(value, unit)}
          className="w-2/3 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        />
        <select
          aria-label="Unidade do tempo médio"
          value={unit}
          onChange={(event) => {
            const nextUnit = event.target.value as TimeUnit;
            setUnit(nextUnit);
            commit(value, nextUnit);
          }}
          className="w-1/3 rounded-md border border-border bg-surface-2 px-1 py-1.5 text-xs text-text-primary"
        >
          {TIME_UNITS.map((u) => (
            <option key={u} value={u}>
              {TIME_UNIT_LABELS[u]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function GoalField({ nodeKey, result }: { nodeKey: string; result: NodeMetricsResult['goal'] }) {
  const updateMetric = useEditorStore((state) => state.updateNodeMetric);
  const current = result.effectiveValue;

  return (
    <div className="space-y-1">
      <span className="text-[11px] text-text-muted">{METRIC_LABELS.goal}</span>
      <div className="flex gap-1">
        <select
          aria-label="Métrica da meta"
          value={current?.metric ?? ''}
          onChange={(event) =>
            updateMetric(nodeKey, 'goal', {
              mode: 'manual',
              manualValue: { metric: event.target.value, value: current?.value ?? 0, period: current?.period },
            })
          }
          className="flex-1 rounded-md border border-border bg-surface-2 px-1 py-1.5 text-xs text-text-primary"
        >
          <option value="">Selecionar…</option>
          {GOAL_TARGET_METRICS.map((metric) => (
            <option key={metric} value={metric}>
              {METRIC_LABELS[metric as NodeMetricKey] ?? metric}
            </option>
          ))}
        </select>
        <input
          aria-label="Valor da meta"
          type="text"
          inputMode="decimal"
          value={current?.value ?? ''}
          onChange={(event) => {
            const parsed = Number(event.target.value.replace(',', '.'));
            if (!current?.metric) return;
            updateMetric(nodeKey, 'goal', {
              mode: 'manual',
              manualValue: { metric: current.metric, value: Number.isNaN(parsed) ? 0 : parsed, period: current.period },
            });
          }}
          disabled={!current?.metric}
          className="w-1/3 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary disabled:opacity-40"
        />
        <select
          aria-label="Período da meta"
          value={current?.period ?? ''}
          disabled={!current?.metric}
          onChange={(event) =>
            current?.metric &&
            updateMetric(nodeKey, 'goal', {
              mode: 'manual',
              manualValue: { metric: current.metric, value: current.value, period: event.target.value || undefined },
            })
          }
          className="flex-1 rounded-md border border-border bg-surface-2 px-1 py-1.5 text-xs text-text-primary disabled:opacity-40"
        >
          <option value="">Sem período</option>
          {GOAL_PERIODS.map((period) => (
            <option key={period} value={period}>
              {GOAL_PERIOD_LABELS[period]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** SPEC-008 §67/plan §25/T070-T075 — the Properties panel "Metrics" section. */
export function MetricsPropertiesSection({
  nodeId,
  supportedMetrics,
  result,
}: {
  nodeId: string;
  supportedMetrics: readonly NodeMetricKey[];
  result: NodeMetricsResult;
}) {
  if (supportedMetrics.length === 0) return null;
  return (
    <div className="space-y-3 border-t border-border-subtle pt-3" data-testid="metrics-section">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-secondary">Métricas</h3>
      {supportedMetrics.map((key) => {
        if (key === 'averageTime') return <AverageTimeField key={key} nodeKey={nodeId} result={result.averageTime} />;
        if (key === 'goal') return <GoalField key={key} nodeKey={nodeId} result={result.goal} />;
        const kind = METRIC_KIND[key] as 'money' | 'count' | 'ratio';
        return <NumericMetricField key={key} nodeKey={nodeId} metricKey={key} result={result[key] as MetricResult} kind={kind} />;
      })}
    </div>
  );
}
